import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamServices } from '../../services/teamServices';
import { useAuth } from '../../services/authentication/authContext';
import {
  Grid2,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box
} from '@mui/material';
import TableSkeleton from '../common/TableSkeleton';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PageHeader from '../ui/PageHeader';
import '../../css/Button.css';
import '../../css/DataTable.css';
import '../../css/Dialog.css';
import { userServices } from '../../services/userServices';
import { useSnackbar } from '../../utils/useSnackbar';

const TEAM_SORT_FIELD_MAP = {
  name: 'name',
  ownerFirstName: 'ownerId.firstName',
  ownerLastName: 'ownerId.lastName',
  ownerEmail: 'ownerId.emailId',
  id: 'id',
  updatedAt: 'updatedAt',
  createdAt: 'createdAt'
};

const Teams = () => {
  const [teamData, setTeamData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { accessToken } = useAuth();
  const [searchTeam, setSearchTeam] = useState('');
  const [debouncedSearchTeam, setDebouncedSearchTeam] = useState(searchTeam);
  const [filteredData, setFilteredData] = useState([]);

  // Dialog State
  const [openTeamDialog, setOpenTeamDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Form Fields
  const [teamName, setTeamName] = useState('');
  const [selectedOwner, setSelectedOwner] = useState('');
  const [selectedNewOwner, setSelectedNewOwner] = useState('');
  const [selectedOwnerName, setSelectedOwnerName] = useState('');
  const [teamId, setTeamId] = useState(null);

  // Data lists
  const [users, setUsers] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [sortModel, setSortModel] = useState([{ field: 'id', sort: 'desc' }]);

  const pageSizeOptions = [5, 10, 15];
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const navigate = useNavigate();
  const isSearchFirstRender = useRef(true);

  // Fetch Main Teams
  const fetchAllTeamsData = useCallback(async () => {
    setLoading(true);
    try {
      const clickedField = sortModel?.[0]?.field || 'id';
      const sortField = TEAM_SORT_FIELD_MAP[clickedField] || clickedField;
      const sortDir = sortModel[0]?.sort || 'desc';
      const response = await teamServices.getAllTeams(
        debouncedSearchTeam,
        paginationModel.page,
        paginationModel.pageSize,
        sortField,
        sortDir
      );
      if (response && Array.isArray(response.content)) {
        setTeamData(response.content);
      }
      setRowCount(response?.totalElements || 0);
    } catch (error) {
      console.error("Error fetching All Team data", error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTeam, paginationModel.page, paginationModel.pageSize, sortModel]);

  // Fetch Eligible Team Owners
  const fetchUsers = async () => {
    try {
      const response = await userServices.getOwnerUsersByTeamScreen();
      setUsers(response || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchAllTeamsData();
      fetchUsers();
    }
  }, [accessToken, fetchAllTeamsData]);

  // Debounced search
  useEffect(() => {
    if (isSearchFirstRender.current) {
      isSearchFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const value = searchTeam.trim();
      if (value === "" || value.length > 2) {
        setDebouncedSearchTeam(value);
        setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTeam]);

  // Filter main teams in memory for instant responsiveness
  useEffect(() => {
    const term = searchTeam.toLowerCase();
    const filtered = teamData?.filter(team =>
      team.name?.toLowerCase().includes(term) ||
      (team.ownerFirstName && team.ownerFirstName.toLowerCase().includes(term)) ||
      (team.ownerLastName && team.ownerLastName.toLowerCase().includes(term)) ||
      (team.ownerEmail && team.ownerEmail.toLowerCase().includes(term)) ||
      (team.ownerId?.firstName && team.ownerId.firstName.toLowerCase().includes(term)) ||
      (team.ownerId?.lastName && team.ownerId.lastName.toLowerCase().includes(term)) ||
      (team.ownerId?.emailId && team.ownerId.emailId.toLowerCase().includes(term))
    );
    setFilteredData(filtered);
  }, [searchTeam, teamData]);

  const handleOpenTeamDialog = (event) => {
    if (event?.currentTarget) event.currentTarget.blur();
    setTeamId(null);
    setIsEditing(false);
    setTeamName('');
    setSelectedOwner('');
    setSelectedNewOwner('');
    setSelectedOwnerName('');
    fetchUsers();
    setOpenTeamDialog(true);
  };

  const handleCloseTeamDialog = () => {
    setOpenTeamDialog(false);
    setTeamId(null);
    setIsEditing(false);
    setTeamName('');
    setSelectedOwner('');
    setSelectedNewOwner('');
    setSelectedOwnerName('');
  };

  const handleFormSubmit = () => {
    if (!teamName || (!teamId && !selectedOwner)) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    if (teamId) {
      const updateData = {
        id: teamId,
        name: teamName,
        ownerId: selectedOwner,
        newOwner: selectedNewOwner || selectedOwner,
      };
      teamServices.updateTeamData(updateData)
        .then(() => {
          fetchUsers();
          showSnackbar('Team updated successfully!', 'success');
          fetchAllTeamsData();
          handleCloseTeamDialog();
        })
        .catch((error) => {
          const msg = error.response?.data?.message || error.response?.data || 'Error updating team';
          showSnackbar(typeof msg === 'string' ? msg : 'Error updating team', 'error');
        });
    } else {
      const createData = {
        name: teamName,
        ownerId: selectedOwner,
      };
      teamServices.postTeamData(createData)
        .then(() => {
          fetchUsers();
          showSnackbar('Team added successfully!', 'success');
          fetchAllTeamsData();
          handleCloseTeamDialog();
        })
        .catch((error) => {
          const msg = error.response?.data?.message || error.response?.data || 'Error creating team';
          showSnackbar(typeof msg === 'string' ? msg : 'Error creating team', 'error');
        });
    }
  };

  const handleEditTeam = useCallback((event, team) => {
    event.stopPropagation();
    if (event?.currentTarget) event.currentTarget.blur();
    setIsEditing(true);
    setTeamId(team.id);
    setTeamName(team.name || '');
    const ownerIdVal = typeof team.ownerId === 'object' ? team.ownerId?.id : team.ownerId;
    setSelectedOwner(ownerIdVal || '');
    setSelectedNewOwner('');
    const ownerNameVal = team.ownerFirstName
      ? `${team.ownerFirstName} ${team.ownerLastName || ''}`
      : `${team.ownerId?.firstName || ''} ${team.ownerId?.lastName || ''}`;
    setSelectedOwnerName(ownerNameVal);
    fetchUsers();
    setOpenTeamDialog(true);
  }, []);

  const handleRowClickTeam = useCallback((rowData) => {
    navigate('/home/team-members', {
      state: {
        teamId: rowData.id,
        teamName: rowData.name,
        isAssistanceTeam: false
      }
    });
  }, [navigate]);

  // Columns for Main Teams
  const teamColumns = useMemo(() => [
    {
      field: 'name', headerName: "Team Name", sortField: 'name', flex: 2.5, minWidth: 150, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'ownerFirstName', headerName: "Owner First Name", sortField: 'ownerId.firstName', flex: 2, minWidth: 140, renderCell: (params) => (
        <Tooltip title={params.value || params.row.ownerId?.firstName}><span>{params.value || params.row.ownerId?.firstName}</span></Tooltip>
      )
    },
    {
      field: 'ownerLastName', headerName: "Owner Last Name", sortField: 'ownerId.lastName', flex: 2, minWidth: 140, renderCell: (params) => (
        <Tooltip title={params.value || params.row.ownerId?.lastName}><span>{params.value || params.row.ownerId?.lastName}</span></Tooltip>
      )
    },
    {
      field: 'ownerEmail', headerName: "Owner Email Id", sortField: 'ownerId.emailId', flex: 2.5, minWidth: 180, renderCell: (params) => (
        <Tooltip title={params.value || params.row.ownerId?.emailId}><span>{params.value || params.row.ownerId?.emailId}</span></Tooltip>
      )
    },
    {
      field: 'actions',
      headerName: "Actions",
      width: 120,
      minWidth: 110,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          <IconButton
            color="primary"
            onClick={(event) => handleEditTeam(event, params.row)}
            className="icon-button"
            size="small"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            color="info"
            onClick={(event) => {
              event.stopPropagation();
              handleRowClickTeam(params.row);
            }}
            className="icon-button"
            size="small"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </div>
      ),
    },
  ], [handleEditTeam, handleRowClickTeam]);

  return (
    <div className="data-table-container">
      <PageHeader title="Teams" />

      {/* Snackbar */}
      {SnackbarComponent}

      <div className="search-and-button">
        <Button
          variant="contained"
          onClick={handleOpenTeamDialog}
          className="add-button"
          startIcon={<AddIcon />}
        >
          Add
        </Button>

        {/* Search Filter */}
        <div className="search-container">
          <input
            type="text"
            placeholder="Search Teams..."
            value={searchTeam}
            onChange={(e) => setSearchTeam(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="data-table">
        <div className="data-grid-container">
          {loading ? (
            <Box sx={{ height: '100%', overflow: 'hidden' }}>
              <TableSkeleton rows={8} columns={6} />
            </Box>
          ) : (
            <DataGrid
              loading={false}
              rows={filteredData}
              columns={teamColumns}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              rowCount={rowCount}
              pageSizeOptions={pageSizeOptions}
              onPaginationModelChange={setPaginationModel}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={(newModel) => {
                if (!newModel?.length && sortModel.length > 0) {
                  setSortModel(sortModel);
                } else {
                  setSortModel(newModel);
                }
              }}
              onRowClick={(params) => handleRowClickTeam(params.row)}
              getRowClassName={() => 'clickable-row'}
            />
          )}
        </div>

        {/* Dialog for Add / Edit Team */}
        <Dialog
          open={openTeamDialog}
          onClose={handleCloseTeamDialog}
          disableRestoreFocus
          slotProps={{ paper: { className: 'modern-dialog' } }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle className="modern-dialog-title">
            <span className="modern-dialog-title-text">
              {isEditing ? "Update Team" : "Add Team"}
            </span>
            <IconButton
              aria-label="close"
              onClick={handleCloseTeamDialog}
              className="modern-dialog-close-btn"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent className="modern-dialog-content">
            <Grid2 container spacing={2.5}>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  label="Team Name"
                  value={teamName ?? ''}
                  onChange={(e) => setTeamName(e.target.value)}
                  fullWidth
                  required
                />
              </Grid2>

              {isEditing ? (
                <>
                  <Grid2 size={{ xs: 12 }}>
                    <TextField
                      label="Current Owner"
                      value={selectedOwnerName ?? ''}
                      fullWidth
                      disabled
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12 }}>
                    <FormControl fullWidth>
                      <InputLabel>New Owner</InputLabel>
                      <Select
                        value={(Array.isArray(users) && users.some((u) => String(u.id) === String(selectedNewOwner))) ? selectedNewOwner : ''}
                        onChange={(e) => setSelectedNewOwner(e.target.value)}
                        label="New Owner"
                      >
                        {users.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {[user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ')}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>
                </>
              ) : (
                <Grid2 size={{ xs: 12 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Team Owner</InputLabel>
                    <Select
                      value={(Array.isArray(users) && users.some((u) => String(u.id) === String(selectedOwner))) ? selectedOwner : ''}
                      onChange={(e) => setSelectedOwner(e.target.value)}
                      label="Team Owner"
                    >
                      {users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {[user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid2>
              )}
            </Grid2>
          </DialogContent>
          <DialogActions className="modern-dialog-actions">
            <Button
              onClick={handleCloseTeamDialog}
              variant="outlined"
              color="inherit"
              sx={{ borderColor: '#e2e8f0', color: '#64748b' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleFormSubmit}
              variant="contained"
              color="primary"
              disableElevation
            >
              {isEditing ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default Teams;
